import React, { useState, useEffect } from "react";
import { get } from "../../utility/fetch2";
import { RiCloseFill } from "react-icons/ri";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper/modules";
import {
  FaTimes,
  FaCalendarAlt,
  FaUserMd,
  FaHeartbeat,
  FaThermometerHalf,
  FaWeight,
  FaNotesMedical,
  FaCapsules,
  FaPills,
  FaProcedures,
} from "react-icons/fa";

import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "./medicalHistory.css";

const MedicalHistory = ({ patientId, closeModal }) => {
  const [patientDetails, setPatientDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState("next");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const detailsResponse = await get(
          `/patients/${patientId}/medicalHistory`,
        );
        setPatientDetails(detailsResponse);
        console.log("Patient Details:", detailsResponse);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [patientId]);

  const handleNextPage = () => {
    if (currentPage < patientDetails.length - 1) {
      setDirection("next");
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setDirection("prev");
      setCurrentPage((prev) => prev - 1);
    }
  };

  const currentPageDetails = patientDetails[currentPage] || {};

  return (
    <div className="overlay">
      <RiCloseFill className="close-btn pointer" onClick={closeModal} />
      <div
        className="modal-box"
        style={{
          width: "95%",
          maxWidth: "900px",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ padding: "clamp(10px, 3vw, 20px)" }}>
          {/* <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                        <img src={logo} alt="Organization Logo" style={{ height: '50px', marginRight: '20px' }} />
                        <h1>Organization Name</h1>
                    </div> */}
          {/* Header */}
          <div className="header">
            {/* <img src={logo} alt="Organization Logo" className="logo" /> */}
            <h1 style={{ fontSize: "clamp(1.2rem, 4vw, 1.5rem)" }}>
              Medical History
            </h1>
          </div>

          <Swiper
            // install Swiper modules
            modules={[Navigation, Pagination, Scrollbar, A11y]}
            spaceBetween={50}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            scrollbar={{ draggable: true }}
            onSwiper={(swiper) => console.log(swiper)}
            onSlideChange={() => console.log("slide change")}
          >
            {patientDetails.map((item, idx) => (
              <SwiperSlide key={idx}>
                {/* Date & Doctor Info */}
                <div
                  className="info-row"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    fontSize: "clamp(0.8rem, 2.5vw, 1rem)",
                  }}
                >
                  <p>
                    <FaCalendarAlt className="icon" />{" "}
                    {new Date(item?.dateOfVisit).toDateString()}
                  </p>
                  <p>
                    <FaUserMd className="icon" /> Dr. {item?.doctor}
                  </p>
                </div>

                {/* Vitals Section */}
                <div className="section">
                  <h2 style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}>
                    Vital Signs
                  </h2>
                  <div
                    className="grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: "8px",
                      fontSize: "clamp(0.75rem, 2.5vw, 0.9rem)",
                    }}
                  >
                    <p>
                      <FaThermometerHalf className="icon" /> Temp:{" "}
                      {item?.temperature}°C
                    </p>
                    <p>
                      <FaHeartbeat className="icon" /> Heart Rate:{" "}
                      {item?.heartPulse} bpm
                    </p>
                    <p>
                      <FaWeight className="icon" /> Weight: {item?.weight} kg
                    </p>
                    <p>
                      <FaNotesMedical className="icon" /> Age: {item?.age}
                    </p>
                    <p>BP: {item?.bloodPressure}</p>
                    <p>Respiratory: {item?.respiratory}</p>
                    {item?.oxygenSaturation && (
                      <p>O₂ Saturation: {item?.oxygenSaturation}%</p>
                    )}
                    {item?.bloodSugar && <p>Blood Sugar: {item?.bloodSugar}</p>}
                  </div>
                </div>

                {/* Diagnosis Section */}
                <div className="section highlight">
                  <h2 style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}>
                    Diagnosis
                  </h2>
                  <p style={{ fontSize: "clamp(0.8rem, 2.5vw, 1rem)" }}>
                    {item?.diagnosis}
                  </p>
                </div>

                {/* Care Plan */}
                <div className="section">
                  <h2 style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}>
                    Doctor's Care Plan
                  </h2>
                  <p style={{ fontSize: "clamp(0.8rem, 2.5vw, 1rem)" }}>
                    {item?.carePlan || "None"}
                  </p>
                </div>

                {/* Medication Details */}
                <div className="section">
                  <h2 style={{ fontSize: "clamp(1rem, 3vw, 1.25rem)" }}>
                    <FaCapsules className="icon" /> Medications
                  </h2>
                  {item?.drugName ? (
                    <p style={{ fontSize: "clamp(0.8rem, 2.5vw, 1rem)" }}>
                      <FaPills className="icon" /> {item?.drugName} -{" "}
                      {item?.quantity} tab(s) x {item?.frequency} daily for{" "}
                      {item?.duration} days
                    </p>
                  ) : (
                    <p style={{ fontSize: "clamp(0.8rem, 2.5vw, 1rem)" }}>
                      No prescribed medications.
                    </p>
                  )}
                  {item?.otherMedicationsQuantity > 0 && (
                    <p style={{ fontSize: "clamp(0.8rem, 2.5vw, 1rem)" }}>
                      Additional Meds: {item?.otherMedicationsQuantity} tab(s) x{" "}
                      {item?.otherMedicationsFrequency} daily for{" "}
                      {item?.otherMedicationsDuration} days
                    </p>
                  )}
                  <p
                    className="note"
                    style={{ fontSize: "clamp(0.75rem, 2.5vw, 0.9rem)" }}
                  >
                    {item?.pharmacistNote}
                  </p>
                </div>

                {/* Admission Status */}
                <p
                  className={`status ${item?.isAdmitted ? "admitted" : "not-admitted"}`}
                  style={{ fontSize: "clamp(0.8rem, 2.5vw, 1rem)" }}
                >
                  {item?.isAdmitted ? "Admitted" : "Not Admitted"}
                </p>

                {/* Close Button */}
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="button-container">
            <button
              className="close-button"
              onClick={closeModal}
              style={{
                fontSize: "clamp(0.8rem, 2.5vw, 1rem)",
                padding: "10px 20px",
              }}
            >
              Close
            </button>
          </div>
          {/* <div className="pagination-controls">
                        <button onClick={handlePreviousPage} disabled={currentPage === 0}>Previous</button>
                        <button onClick={handleNextPage} disabled={currentPage === patientDetails.length - 1}>Next</button>
                    </div> */}
        </div>
      </div>
    </div>
  );
};

export default MedicalHistory;
